import { useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import { useMemo } from "react";
import { EmptyState, StateMatrix, cn } from "@/shared/ui";
import { ctMimeToBadgeLabel } from "./code-file-reader";
import { documentsQuery } from "./queries";

/**
 * CatalogDetailPage now just hosts the Outlet (reader or index pane).
 * The document tree moved into the AppShell sidebar so we have a single
 * unified navigation surface.
 */
export function CatalogDetailPage() {
  return (
    <section className="min-w-0 overflow-auto">
      <Outlet />
    </section>
  );
}

export function CatalogIndexPane() {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <p className="text-sm text-[var(--fg-muted)]">
        Select a document from the sidebar to begin reading.
      </p>
    </div>
  );
}

/**
 * The document tree for a single catalog, grouped by category with sticky
 * headers. Rendered inside the sidebar body when route is /catalogs/:name/*.
 */
export function CatalogDocTree({ catalog }: { catalog: string }) {
  const q = useQuery(documentsQuery(catalog));

  const grouped = useMemo(() => {
    const data = q.data ?? [];
    const map = new Map<string, typeof data>();
    for (const d of data) {
      const cat = d.category ?? "other";
      const arr = map.get(cat) ?? [];
      arr.push(d);
      map.set(cat, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [q.data]);

  return (
    <StateMatrix
      status={q.status}
      data={q.data}
      error={q.error}
      onRetry={() => q.refetch()}
      isEmpty={(d) => d.length === 0}
      empty={<EmptyState title="No documents" />}
    >
      {() => (
        <ul className="flex flex-col gap-4">
          {grouped.map(([cat, docs]) => (
            <li key={cat}>
              <h2 className="sticky top-0 z-10 mb-1 border-b border-[var(--border)] bg-[var(--surface-2)]/95 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--accent)] backdrop-blur">
                {cat}
                <span className="ml-1.5 text-[var(--fg-muted)]">{docs.length}</span>
              </h2>
              <ul className="flex flex-col">
                {docs.map((d) => (
                  <DocLink
                    key={d.id}
                    catalog={catalog}
                    slug={d.slug}
                    title={d.title ?? prettifyLeaf(leafOf(d.slug))}
                    {...(d.contentType ? { contentType: d.contentType } : {})}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </StateMatrix>
  );
}

function leafOf(slug: string): string {
  const parts = slug.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? slug;
}
function prettifyLeaf(leaf: string): string {
  const spaced = leaf.replace(/[-_]+/g, " ").trim();
  if (!spaced) return leaf;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function DocLink({
  catalog,
  slug,
  title,
  contentType,
}: {
  catalog: string;
  slug: string;
  title: string;
  contentType?: string;
}) {
  const badge = ctMimeToBadgeLabel(contentType);
  return (
    <li>
      <Link
        to="/catalogs/$name/$"
        params={{ name: catalog, _splat: slug }}
        className={cn(
          "relative flex items-center gap-2 rounded-md px-3 py-1 text-[13px] text-[var(--fg-muted)] transition-colors hover:bg-[var(--surface)] hover:text-[var(--fg)]",
        )}
        activeProps={{
          className:
            "bg-[var(--accent)]/10 font-medium text-[var(--fg)] before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded before:bg-[var(--accent)]",
        }}
      >
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {badge && (
          <span className="shrink-0 rounded bg-[var(--surface)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--fg-muted)]">
            {badge}
          </span>
        )}
      </Link>
    </li>
  );
}
